import { cloneDeep, isNil, isNumber } from 'lodash'
import { actions } from '../../../reducers/assignment.reducer'
import { submissionApi } from '../../submission.api'
import { findSubmissionIndex } from '../../../../utils/cluster.helper'
import baseApi from '../../base.api'
import { SubmissionReviewStatuses } from '../../../../constants/submission-review-statuses'
import SubmissionApproveStatuses from '../../../../constants/submission-approve-statuses'
import {
    convertPerQuestionDiagramToTotal,
    convertPerQuestionDiagramToTotalCount,
} from '../../../../utils/model-progress.helper'
import { ExplainabilityStatuses } from '../../../../constants/explainability-statuses'
import { isMba } from '../../../../utils/assignment.helper'
import { updateMatchingCacheEntries } from './manualCacheUpdateUtils'

const updateQuestionAnswersCaches = (dispatch, questionRubricId, versionId, updateFunction) => {
    if (!isNumber(questionRubricId)) return []

    // Use the reusable cache update utility
    return updateMatchingCacheEntries(
        dispatch,
        'fetchQuestionAnswers',
        params => params.id === questionRubricId && params.versionId === versionId,
        updateFunction,
    )
}

const updateTextAssignmentSubmissionsCaches = (dispatch, versionId, token, updateFunction) => {
    // Use the reusable cache update utility
    return updateMatchingCacheEntries(
        dispatch,
        'fetchTextAssignmentSubmissions',
        params => params.versionId === versionId && params.token === token,
        updateFunction,
    )
}

export const onReviewSubmissionQueryStarted = async (
    { token, layerId, submissionId, newPatternId, newReasoning, isPatternCorrected, removePattern },
    { dispatch, getState, queryFulfilled },
) => {
    const response = await queryFulfilled
    const prevClusters = cloneDeep(getState().assignment.clusters)
    let fileToAdd = null

    let clustersWithFileManipulated
    if (isPatternCorrected || removePattern) {
        clustersWithFileManipulated = prevClusters.map(prevCluster => {
            const filteredFiles = prevCluster.files.filter(file => {
                if (file.id === submissionId) {
                    fileToAdd = file
                    return false
                }
                return true
            })
            return {
                ...prevCluster,
                files: filteredFiles,
            }
        })

        if (isNil(newPatternId)) {
            fileToAdd.motifList = []
        } else if (fileToAdd.motifList.length) {
            const newPatternColorId = getState().assignment.assignment.patterns.find(
                pattern => pattern.motif_id === newPatternId,
            ).color_id
            fileToAdd.motifList.map(pattern => {
                pattern.motif_id = newPatternId
                pattern.color_id = newPatternColorId
                pattern.reasoning = newReasoning
            })
        } else {
            const newPatternColorId = getState().assignment.assignment.patterns.find(
                pattern => pattern.motif_id === newPatternId,
            ).color_id
            fileToAdd.motifList.push({
                motif_id: newPatternId,
                color_id: newPatternColorId,
                start_row: 1,
                end_row: fileToAdd.seq_size,
                reasoning: newReasoning,
            })
        }

        clustersWithFileManipulated = clustersWithFileManipulated.map(cluster => {
            if (cluster.cluster_id === response.data.newClusterId && fileToAdd)
                return {
                    ...cluster,
                    files: [...cluster.files, fileToAdd],
                }
            return cluster
        })
    }

    const { data: reviews } = submissionApi.endpoints.fetchSubmissionReviews.select({
        token,
        layerId,
    })(getState())
    const allSubmissions = cloneDeep(
        getState()
            .assignment.clusters.map(cluster => cluster.files)
            .flat(),
    )
    const prevSubmissionIndex = findSubmissionIndex(getState().assignment.clusters, submissionId)
    const findUnreviewedSubmission = submissions =>
        submissions.find(submission => !reviews.some(review => review.submissionId === submission.id))
    let nextUnreviewedSubmission =
        findUnreviewedSubmission(allSubmissions.slice(prevSubmissionIndex + 1)) ||
        findUnreviewedSubmission(allSubmissions.slice(0, prevSubmissionIndex))

    if (nextUnreviewedSubmission) {
        dispatch(actions.setDrawerFile(nextUnreviewedSubmission))
        document.getElementById(`cluster__file-card-${nextUnreviewedSubmission.id}`).scrollIntoView({ block: 'center' })
    }

    if (clustersWithFileManipulated) dispatch(actions.setClusters(clustersWithFileManipulated))
    dispatch(
        baseApi.util.updateQueryData('fetchSubmissionReviews', { token, layerId }, draft => {
            draft.unshift(response.data.review)
        }),
    )
    dispatch(
        baseApi.util.updateQueryData('fetchSubmissionCriteriaDetails', { token, layerId, submissionId }, draft => {
            draft[newPatternId] = newReasoning
        }),
    )
}

export const onReviewQuestionRubricAnswerQueryStarted = async (
    {
        versionId,
        id,
        categoryId,
        reasoning,
        explainabilityReasoning,
        explainabilityStatus,
        highlights,
        mismatchId,
        submissionId,
        questionRubricId,
        token,
        assignmentType,
        cannedFeedback,
    },
    { dispatch, getState, queryFulfilled },
) => {
    let previousAnswer
    let allApproved = false
    const fetchTextSubmissionDetailsPatchResult = dispatch(
        baseApi.util.updateQueryData('fetchTextSubmissionDetails', { submissionId }, draft => {
            const answer = draft.answers.find(
                answer => answer.id === id && answer.questionRubricId === questionRubricId,
            )
            previousAnswer = cloneDeep(answer)
            if (answer) {
                const categoryChanged = answer.categoryId !== categoryId
                answer.categoryId = categoryId
                answer.approveStatus = SubmissionApproveStatuses.APPROVED
                answer.actor = {
                    id: getState().auth.user?.id,
                    email: getState().auth.user?.email,
                    name: getState().auth.user?.name,
                }
                answer.confidence = categoryChanged ? null : answer.confidence
                answer.createdAt = new Date().toISOString()
                answer.reasoning = reasoning
                answer.escalated = explainabilityStatus
                answer.isAdequate = explainabilityStatus !== ExplainabilityStatuses.UNEXPLAINABLE_UNRESOLVED
                answer.explainabilityReasoning = explainabilityReasoning
                answer.highlights = highlights
                if (categoryChanged) {
                    answer.personalizedFeedback = reasoning
                    answer.cannedFeedback = cannedFeedback
                }
            }
            else {
                draft.answers = [...draft.answers, {
                    id,
                    questionRubricId,
                    categoryId,
                    reasoning,
                    explainabilityReasoning,
                    explainabilityStatus,
                    highlights,
                    cannedFeedback,
                    confidence: null,
                    createdAt: new Date().toISOString(),
                    escalated: explainabilityStatus,
                    isAdequate: explainabilityStatus !== ExplainabilityStatuses.UNEXPLAINABLE_UNRESOLVED,
                    approveStatus: SubmissionApproveStatuses.APPROVED,
                    actor: {
                        id: getState().auth.user?.id,
                        email: getState().auth.user?.email,
                        name: getState().auth.user?.name,
                    },
                }]
            }

            allApproved = draft.answers.every(answer => answer.approveStatus !== SubmissionApproveStatuses.NOT_APPROVED)
        }),
    )

    const fetchAnswerCategoryHistoryPatchResult = dispatch(
        baseApi.util.updateQueryData('fetchAnswerCategoryHistory', { id }, draft => {
            draft?.unshift(previousAnswer)
        }),
    )

    // Update all fetchQuestionAnswers cache variations
    const updateAnswerData = draft => {
        const answer = draft?.find(answer => answer.id === submissionId)
        if (answer) {
            answer.categoryId = categoryId
            answer.approveStatus = SubmissionApproveStatuses.APPROVED
            answer.actor = {
                id: getState().auth.user?.id,
                email: getState().auth.user?.email,
                name: getState().auth.user?.name,
            }
            answer.confidence = null
            answer.reviewStatus = SubmissionReviewStatuses.REVIEWED
            answer.escalated = explainabilityStatus
            answer.isAdequate = explainabilityStatus !== ExplainabilityStatuses.UNEXPLAINABLE_UNRESOLVED
        }
    }

    const fetchQuestionAnswersPatchResults = updateQuestionAnswersCaches(
        dispatch,
        questionRubricId,
        versionId,
        updateAnswerData,
    )

    const updateTextSubmissionData = draft => {
        const answer = draft?.find(answer => answer.id === submissionId)

        if (answer) {
            answer.reviewStatus = allApproved
                ? SubmissionReviewStatuses.REVIEWED
                : SubmissionReviewStatuses.PARTIALLY_REVIEWED
        }
    }

    const fetchTextAssignmentSubmissionsPatchResults = updateTextAssignmentSubmissionsCaches(
        dispatch,
        versionId,
        token,
        updateTextSubmissionData,
    )

    let fetchProgressPatchResult

    const mba = isMba(assignmentType)

    if (mismatchId || mba) {
        fetchProgressPatchResult = dispatch(
            baseApi.util.updateQueryData(
                'fetchProgress',
                {
                    token: token,
                    versionId: versionId,
                },
                draft => {
                    if (mba) {
                        if (!draft.originalModel.review_results[questionRubricId]?.left){
                            return
                        }

                        draft.originalModel.review_results[questionRubricId].left -= 1
                        draft.reviewResults = draft.originalModel.review_results
                        draft.totalReviewResults = convertPerQuestionDiagramToTotal(draft.originalModel.review_results)
                    } else {
                        const isTrained = draft.originalModel.train_questions[questionRubricId]

                        if (isTrained) {
                            draft.originalModel.review_training_mismatches[questionRubricId].left -= 1
                            draft.reviewTrainingMismatches = draft.originalModel.review_training_mismatches
                            draft.totalReviewTrainingMismatches = convertPerQuestionDiagramToTotalCount(
                                draft.originalModel.review_training_mismatches,
                            )
                        } else {
                            draft.originalModel.review_flagging[questionRubricId].left -= 1
                            draft.reviewFlagging = draft.originalModel.review_flagging
                            draft.totalReviewFlagging = convertPerQuestionDiagramToTotalCount(
                                draft.originalModel.review_flagging,
                            )
                        }
                    }
                },
            ),
        )
    }

    try {
        await queryFulfilled
    } catch {
        fetchTextSubmissionDetailsPatchResult.undo()
        fetchAnswerCategoryHistoryPatchResult?.undo()
        fetchQuestionAnswersPatchResults.forEach(patchResult => patchResult?.undo())
        fetchTextAssignmentSubmissionsPatchResults.forEach(patchResult => patchResult?.undo())
        fetchProgressPatchResult?.undo()
    }
}

export const onUpdateQuestionAnswerQueryStarted = async (
    { id, feedback, submissionId },
    { dispatch, queryFulfilled },
) => {
    try {
        await queryFulfilled
        dispatch(
            baseApi.util.updateQueryData('fetchTextSubmissionDetails', { submissionId }, draft => {
                const answer = draft.answers.find(answer => answer.id === id)
                if (answer && feedback !== undefined) answer.feedback = feedback
            }),
        )
    } catch (error) {
        console.error('Error updating question answer:', error)
    }
}

export const onUpdateQuestionAnswerNoteQueryStarted = async (
    { id, note, submissionId },
    { dispatch, queryFulfilled },
) => {
    try {
        await queryFulfilled
        dispatch(
            baseApi.util.updateQueryData('fetchTextSubmissionDetails', { submissionId }, draft => {
                const answer = draft.answers.find(answer => answer.id === id)
                if (answer && note !== undefined) answer.note = note
            }),
        )
    } catch (error) {
        console.error('Error updating note:', error)
    }
}

export const onUpdateQuestionAnswerEscalatedQueryStarted = async (
    { id, escalated, note, submissionId, questionId, questionRubricId, versionId, token },
    { dispatch, getState, queryFulfilled },
) => {
    const fetchTextSubmissionDetailsPatchResult = dispatch(
        baseApi.util.updateQueryData('fetchTextSubmissionDetails', { submissionId }, draft => {
            const answer = draft.answers.find(answer => {
                return answer.id === id && answer.questionRubricId === questionRubricId
            })
            if (answer && escalated !== undefined) answer.escalated = escalated
            if (answer && note !== undefined) answer.note = note
        }),
    )

    const updateEscalatedTextSubmissionData = draft => {
        const answer = draft?.find(answer => answer.id === submissionId)
        if (answer) {
            const state = getState()

            const fetchTextSubmissionDetailsData = baseApi.endpoints.fetchTextSubmissionDetails.select({
                submissionId,
            })(state).data

            if (fetchTextSubmissionDetailsData?.answers) {
                answer.escalated = fetchTextSubmissionDetailsData.answers.some(answer =>
                    answer.id === id ? escalated : answer.escalated,
                )
            }
        }
    }

    const fetchTextAssignmentSubmissionsPatchResults = updateTextAssignmentSubmissionsCaches(
        dispatch,
        versionId,
        token,
        updateEscalatedTextSubmissionData,
    )

    // Update all fetchQuestionAnswers cache variations
    const updateAnswerEscalated = (draft, params) => {
        const answerIndex = draft?.findIndex(answer => answer.id === submissionId)
        if (answerIndex === -1 || answerIndex === undefined) return

        const answer = draft[answerIndex]

        // If the query filters by escalated status and we're unflagging, remove from list
        const isUnflagging =
            escalated === ExplainabilityStatuses.MANUAL_RESOLVED ||
            escalated === ExplainabilityStatuses.UNEXPLAINABLE_RESOLVED
        const hasEscalatedFilter = params?.onlyEscalated || params?.onlySelfEscalated

        if (isUnflagging && hasEscalatedFilter) {
            // Remove from the filtered list
            draft.splice(answerIndex, 1)
        } else {
            // Just update the escalated status
            answer.escalated = escalated
        }
    }

    // Use questionRubricId for cache updates if provided, fallback to questionId for backward compatibility
    const fetchQuestionAnswersPatchResults = updateQuestionAnswersCaches(
        dispatch,
        questionRubricId || questionId,
        versionId,
        updateAnswerEscalated,
    )

    try {
        await queryFulfilled
    } catch {
        fetchTextSubmissionDetailsPatchResult.undo()
        fetchTextAssignmentSubmissionsPatchResults.forEach(patchResult => patchResult?.undo())
        fetchQuestionAnswersPatchResults.forEach(patchResult => patchResult?.undo())
    }
}

export const onUpdateQuestionAnswerReasoningQueryStarted = async (
    { id, submissionId, questionRubricId, feedbackType = 'personalized' },
    { dispatch, getState, queryFulfilled },
) => {
    let previousAnswer
    try {
        const { data } = await queryFulfilled

        dispatch(
            baseApi.util.updateQueryData('fetchTextSubmissionDetails', { submissionId }, draft => {
                const answer = draft.answers.find(
                    answer => answer.id === id && answer.questionRubricId === questionRubricId,
                )
                if (answer && data) {
                    previousAnswer = cloneDeep(answer)

                    if (feedbackType === 'personalized') {
                        answer.personalizedFeedback = data.reasoning
                    } else {
                        answer.cannedFeedback = data.reasoning
                    }

                    answer.actor = {
                        id: getState().auth.user?.id,
                        email: getState().auth.user?.email,
                        name: getState().auth.user?.name,
                    }
                    answer.createdAt = new Date().toISOString()
                }
            }),
        )

        if (previousAnswer) {
            dispatch(
                baseApi.util.updateQueryData('fetchAnswerCategoryHistory', { id }, draft => {
                    draft?.unshift(previousAnswer)
                }),
            )
        }
    } catch (error) {
        console.error('Error updating question answer:', error)
    }
}

export const onUpdateTextSubmissionQueryStarted = async ({ id, feedback }, { dispatch, queryFulfilled }) => {
    try {
        await queryFulfilled
        dispatch(
            baseApi.util.updateQueryData('fetchTextSubmissionDetails', { submissionId: id }, draft => {
                draft.feedback = feedback
            }),
        )
    } catch (error) {
        console.error('Error updating text submission:', error)
    }
}
